import {
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  Paginate,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';

import { FilterAltIcon } from 'cokitchen-icons';
import React, { useCallback, useEffect, useState } from 'react';
import './styles.scss';
import { debounce, formatCurrency } from 'utils/functions';
import useInventoryStoreContext from 'contexts/inventory-store.context/context';
import { StoreItemType } from 'models/store-item.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import useDashboardContext from 'contexts/dashboard.context';
const tableHeaders = ['Item name', 'Min.Qty(g)', 'Price(per g)', 'Unit (per g)', ''];

const IngredientList = () => {
  const { setStoreItemsToEdit } = useInventoryStoreContext();
  const [storeArray, setStoreArray] = useState<StoreItemType[]>([]);
  const [isFetchingStoreItems, setIsFetchingStoreItems] = useState(false);
  const [totalStoreItems, setTotalStoreItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');

  const { formIngredientCategoryList } = useDashboardContext();
  const CATEGORY_LIST: string[] = formIngredientCategoryList;

  const fetchStoreItems = async (
    searchString: string,
    categoryString: string,
    current_page?: number
  ) => {
    try {
      setIsFetchingStoreItems(true);
      const response = await apiService.get(
        `ingredients/with-vendors?search=${searchString}&sort=${categoryString}&page=${
          current_page || '1'
        }`
      );
      if (response) {
        setStoreArray(response?.data?.results);
        setLastPage(Math.ceil(response?.data?.total / 10));
        setTotalStoreItems(response?.data?.total);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching store items'}
          type='error'
        />
      );
    } finally {
      setIsFetchingStoreItems(false);
    }
  };

  const debounceFetchStoreItems = useCallback(debounce(fetchStoreItems, 1000), []);

  useEffect(() => {
    setIsFetchingStoreItems(true);
    clearStoreItemList();
    debounceFetchStoreItems(search, categoryFilter, currentPage);
  }, [search, categoryFilter, currentPage]);

  const clearStoreItemList = () => {
    setStoreArray([]);
    setStoreItemsToEdit([]);
  };

  return (
    <div className='px-2 md:px-6 mt-2 vendors'>
      <div className='flex justify-between items-center mb-8'>
        <div>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>
            Ingredients ({totalStoreItems ? totalStoreItems : isFetchingStoreItems ? '**' : ''})
          </h4>
          <p className='text-[#6F7376] text-sm'>Manage and track the quantity of items in stock</p>
        </div>
      </div>
      <div className='flex justify-between items-center mb-8'>
        <div className='flex gap-3 items-center'>
          <div className='h-[36px] mt-2'>
            <Dropdown
              hideArrow
              placement='bottom-start'
              content={
                <div className='border border-[#E5E6E6] rounded-[5px] text-xs leading-[19px] h-[36px] flex px-2 justify-between items-center w-[114px]  cursor-pointer'>
                  <span className='text-[#5E6164] text-sm truncate'>{categoryFilter}</span>
                  <FilterAltIcon className='text-[#5E6164]' />
                </div>
              }>
              <DropdownList>
                {['All', ...CATEGORY_LIST].map((item, i) => (
                  <DropdownItem
                    className={`capitalize ${categoryFilter === item ? 'text-red-5' : ''}`}
                    key={i}
                    onClick={() => setCategoryFilter(item)}>
                    {item}
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown>
          </div>
          <Input
            type='search'
            placeholder='Search ingredients'
            className='w-[300px]'
            inputClass='h-[36px]'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => {
            clearStoreItemList();
            setCurrentPage(e.selected + 1);
          }}
        />
      </div>
      <Table headers={tableHeaders} loading={isFetchingStoreItems}>
        {storeArray.map((storeItem) => (
          <TableRow key={storeItem.id} className='hover:bg-[#F5F6F6] table_row'>
            <TableData>{storeItem.name}</TableData>
            <TableData>{formatCurrency(storeItem.min_quantity, { style: 'decimal' })}</TableData>
            <TableData>{formatCurrency(storeItem.min_vendor_price)}</TableData>
            <TableData>{storeItem.unit_weight}</TableData>
          </TableRow>
        ))}
      </Table>
    </div>
  );
};

export default IngredientList;
